import {
  useErrorHandlingMutation,
  useErrorHandlingQuery,
} from '@backoffice-frontend/data-fetching'
import { Box, UnstyledButton } from '@backoffice-frontend/patterns'
import { useBackofficeStatusesQuery } from '@backoffice-frontend/status-api'
import { useBackofficeUpdatesQuery } from '@backoffice-frontend/updates-api'
import { useBackofficeLoggedInUser } from '@backoffice-frontend/user-management-ui'
import {
  ActionItem,
  ActionMenu,
  Avatar,
  useActionMenu,
  useSelectionMenu,
} from '@moia-dev/pace-web'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useBackofficeStatusGetLastVisitForBadgeQuery,
  useBackofficeStatusSetLastVisitMutation,
  useBackofficeUpdateGetLastVisitForBadgeQuery,
  useBackofficeUpdateSetLastVisitMutation,
} from './AccountBadge.hook'
import { AccountBadgeGroupSwitcher } from './AccountBadgeMenu/AccountBadgeGroupSwitcher/AccountBadgeGroupSwitcher'
import { AccountBadgeLanguageSwitch } from './AccountBadgeMenu/AccountBadgeLanguageSwitch'
import { AccountBadgeMainMenu } from './AccountBadgeMenu/AccountBadgeMainMenu'
import { AccountBadgeThemeSwitch } from './AccountBadgeMenu/AccountBadgeThemeSwitch'
import { MenuState, type MenuStateType } from './AccountBadgeMenu/MenuState'
import { NotificationBadge } from './NotificationBadge'

const MENU_WIDTH = 210

/** Display name for pace-web Avatar (produces same initials as getUserInitials) */
const getAvatarName = (username: string): string =>
  username ? username.replace('.', ' ') : ''

/** Initials for button accessible name (matches pace-web Avatar getInitials for size 32) */
const getInitials = (name: string): string => {
  if (!name.trim()) return ''
  const segments = name.trim().split(' ')
  const first = segments[0].charAt(0)
  const last = segments[segments.length - 1].charAt(0)
  return segments.length === 1
    ? first.toUpperCase()
    : (first + last).toUpperCase()
}

export const AccountBadge = () => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [isGroupSwitcherOpen, setIsGroupSwitcherOpen] = useState(false)
  const [currentMenu, setCurrentMenu] = useState<MenuStateType>(MenuState.main)

  const { user: loggedInUser } = useBackofficeLoggedInUser()

  const avatarName = loggedInUser ? getAvatarName(loggedInUser.username) : ''

  const { data: updatesData } = useBackofficeUpdatesQuery()

  const { data: statusesData } = useErrorHandlingQuery(
    useBackofficeStatusesQuery,
    {
      variables: {},
    },
  )
  const { data: statusLastVisit, refetch: refetchStatusLastVisit } =
    useErrorHandlingQuery(useBackofficeStatusGetLastVisitForBadgeQuery, {
      variables: {},
    })
  const { data: updateLastVisit, refetch: refetchUpdateLastVisit } =
    useErrorHandlingQuery(useBackofficeUpdateGetLastVisitForBadgeQuery, {
      variables: {},
    })

  const [setUpdatesLastVisited] = useErrorHandlingMutation(
    useBackofficeUpdateSetLastVisitMutation,
  )
  const [setStatusLastVisited] = useErrorHandlingMutation(
    useBackofficeStatusSetLastVisitMutation,
  )

  const hasNotificationsUpdates =
    !!updatesData?.backofficeUpdates.updates.some(
      update =>
        update?.creationDate &&
        updateLastVisit?.lastVisit.lastVisitDate &&
        update.creationDate > updateLastVisit?.lastVisit.lastVisitDate,
    ) || false

  const hasNotificationsStatuses =
    !!statusesData?.backofficeStatuses.statuses.some(
      status =>
        status?.creationDate &&
        statusLastVisit?.lastVisit.lastVisitDate &&
        status.creationDate > statusLastVisit?.lastVisit.lastVisitDate,
    ) || false

  const { getReferenceProps, ...props } = useActionMenu({
    isOpen,
    setIsOpen,
  })
  const groupSwitcherSelectionMenu = useSelectionMenu({
    isOpen: isGroupSwitcherOpen,
    setIsOpen: setIsGroupSwitcherOpen,
  })

  useEffect(() => {
    if (!isOpen) {
      setCurrentMenu(MenuState.main)
    }
    if (isOpen) {
      setIsGroupSwitcherOpen(false)
    }
  }, [isOpen])

  return (
    <>
      <Box {...getReferenceProps()} ref={props.refs.setReference}>
        <UnstyledButton
          aria-label={
            avatarName
              ? getInitials(avatarName)
              : t('authenticatedAppView_account_button')
          }
          ref={groupSwitcherSelectionMenu.refs.setReference}
        >
          <NotificationBadge active={hasNotificationsUpdates}>
            <Avatar
              name={avatarName}
              size={32}
              className={isOpen ? 'account-badge-avatar-active' : undefined}
            />
          </NotificationBadge>
        </UnstyledButton>
        <ActionMenu {...props}>
          <motion.div
            animate={{
              transition: { duration: 0.2, ease: 'easeInOut' },
            }}
            style={{ overflow: 'hidden', width: MENU_WIDTH }}
          >
            <AnimatePresence mode="wait" initial={false}>
              {currentMenu === MenuState.main && (
                <AccountBadgeMainMenu
                  setCurrentMenu={setCurrentMenu}
                  setIsOpen={setIsOpen}
                  hasNotificationsUpdates={hasNotificationsUpdates}
                  hasNotificationsStatuses={hasNotificationsStatuses}
                  setUpdatesLastVisited={setUpdatesLastVisited}
                  refetchUpdateLastVisit={refetchUpdateLastVisit}
                  setStatusLastVisited={setStatusLastVisited}
                  refetchStatusLastVisit={refetchStatusLastVisit}
                  groupSwitcherActionItem={
                    <ActionItem
                      {...groupSwitcherSelectionMenu.getReferenceProps({
                        onClick: () => setIsOpen(false),
                      })}
                      label={t('authenticatedAppView_groupSwitcher_button')}
                    />
                  }
                />
              )}
              {currentMenu === MenuState.language && (
                <AccountBadgeLanguageSwitch setCurrentMenu={setCurrentMenu} />
              )}
              {currentMenu === MenuState.theme && (
                <AccountBadgeThemeSwitch setCurrentMenu={setCurrentMenu} />
              )}
            </AnimatePresence>
          </motion.div>
        </ActionMenu>
      </Box>
      {loggedInUser && isGroupSwitcherOpen && (
        <AccountBadgeGroupSwitcher
          selectionMenuProps={groupSwitcherSelectionMenu}
          user={loggedInUser}
        />
      )}
    </>
  )
}
